/**
 * Service Module Configurations
 *
 * Hardcoded service definitions including field requirements,
 * pre-site rules, and display metadata.
 *
 * Future: Move to Firestore `service_module_configs` collection.
 */

import { DealCategory } from '@/types/theo-workflow.types';

export interface ServiceConfig {
  category: DealCategory;
  label: string;
  icon: string; // Lucide icon name
  color: string; // Tailwind color class
  presiteDefault: 'always' | 'never' | 'conditional';
  presiteCondition?: string; // Human-readable condition
  intakeFieldDefs: FieldDef[];
  presiteFieldDefs: FieldDef[];
  postQuoteFieldDefs: FieldDef[];
}

export interface FieldDef {
  key: string;
  label: string;
  type: 'text' | 'boolean' | 'select' | 'number' | 'textarea' | 'multi-text' | 'button-group';
  options?: string[];
  required?: boolean;
  placeholder?: string;
  allowCustom?: boolean; // Show "Other" option with text input on selects/button-groups
  dynamicOptions?: 'service_plans'; // Load options dynamically from Firestore
  showPricing?: boolean; // Show pricing info for service plans (default: false)
  conditionalOn?: { field: string; value: unknown }; // Only show this field if another field has a specific value
}

export const SERVICE_CONFIGS: Record<DealCategory, ServiceConfig> = {
  [DealCategory.NBN]: {
    category: DealCategory.NBN,
    label: 'NBN',
    icon: 'Globe',
    color: 'blue',
    presiteDefault: 'never',
    intakeFieldDefs: [
      { key: 'byoModem', label: 'BYO Modem?', type: 'boolean' },
      { key: 'servicePlan', label: 'Service Plan', type: 'select', dynamicOptions: 'service_plans', showPricing: true, allowCustom: true },
      { key: 'notes', label: 'Notes', type: 'textarea', placeholder: 'Any additional notes...' },
    ],
    presiteFieldDefs: [],
    postQuoteFieldDefs: [
      { key: 'avcNumber', label: 'AVC Number', type: 'text', placeholder: 'AVC...' },
    ],
  },

  [DealCategory.STARLINK]: {
    category: DealCategory.STARLINK,
    label: 'Starlink',
    icon: 'Satellite',
    color: 'indigo',
    presiteDefault: 'never',
    intakeFieldDefs: [
      { key: 'starlinkKit', label: 'Kit Option', type: 'button-group', options: ['TECHERY', 'BYO'], required: true, allowCustom: false },
      { key: 'starlinkModel', label: 'Starlink Model', type: 'button-group', options: ['Standard', 'Mini'], required: false, allowCustom: false },
      { key: 'servicePlan', label: 'Service Plan', type: 'select', dynamicOptions: 'service_plans', showPricing: true, allowCustom: true, conditionalOn: { field: 'starlinkKit', value: 'TECHERY' } },
      // Self-managed = customer's own Starlink account (the "S/M" kit) rather
      // than Techery holding the account and rebilling. Independent of the
      // kit option above — a Techery-supplied kit can still be self-managed.
      { key: 'starlinkSelfManaged', label: 'Self-Managed Service?', type: 'boolean' },
      { key: 'roofType', label: 'Roof Type', type: 'button-group', options: ['Colourbond/Tin', 'Tile', 'Klip Lock', 'Unknown'], required: false, allowCustom: false },
      { key: 'stories', label: 'Stories', type: 'button-group', options: ['Single Story', 'Double Story', 'Unknown'], required: false, allowCustom: false },
      { key: 'notes', label: 'Notes', type: 'textarea', placeholder: 'Any additional notes...' },
    ],
    presiteFieldDefs: [],
    postQuoteFieldDefs: [
      { key: 'kitNumber', label: 'Kit Number', type: 'text' },
      { key: 'starlinkAccountNumber', label: 'Starlink Account Number', type: 'text', placeholder: 'ACC-XXXXXXX-XXXXX-XX', required: false },
    ],
  },

  [DealCategory.VOIP]: {
    category: DealCategory.VOIP,
    label: 'VoIP',
    icon: 'Phone',
    color: 'emerald',
    presiteDefault: 'conditional',
    presiteCondition: 'If complex call flows selected',
    intakeFieldDefs: [
      { key: 'servicePlan', label: 'Service Plan', type: 'select', dynamicOptions: 'service_plans', showPricing: true, allowCustom: true },
      { key: 'sasbossCustomerName', label: 'Sasboss Customer Name', type: 'text', placeholder: 'Customer name in Sasboss portal' },
      { key: 'mainNumber', label: 'Main Business Number', type: 'text', placeholder: 'e.g. 02 4000 0000' },
      { key: 'handsetCount', label: 'Handsets On-Site', type: 'number', placeholder: 'e.g. 4' },
      { key: 'byoHandsets', label: 'BYO Handsets?', type: 'boolean' },
      { key: 'portingRequired', label: 'Porting Required?', type: 'boolean' },
      { key: 'numbersToPort', label: 'Numbers to Port', type: 'multi-text', placeholder: 'Enter number...' },
      { key: 'complexity', label: 'Complexity', type: 'button-group', options: ['Simple', 'Complex'], required: true },
      { key: 'notes', label: 'Notes', type: 'textarea', placeholder: 'Any additional notes...' },
    ],
    presiteFieldDefs: [
      { key: 'callFlowNotes', label: 'Call Flow Design Notes', type: 'textarea', placeholder: 'Queues, auto-attendant, after-hours routing, call groups...' },
      { key: 'handsetLocations', label: 'Handset Locations', type: 'textarea', placeholder: 'e.g. Reception x2, warehouse x1, office x3' },
      { key: 'extensionCount', label: 'Extensions Configured', type: 'number' },
    ],
    postQuoteFieldDefs: [
      { key: 'portingRequestDate', label: 'Porting Request Sent', type: 'text' },
      { key: 'portingStatus', label: 'Porting Status', type: 'select', options: ['pending', 'submitted', 'approved', 'completed'] },
    ],
  },

  [DealCategory.IT]: {
    category: DealCategory.IT,
    label: 'IT',
    icon: 'Monitor',
    color: 'purple',
    presiteDefault: 'always',
    intakeFieldDefs: [
      { key: 'description', label: 'Description of Needs', type: 'textarea', required: true },
      { key: 'notes', label: 'Notes', type: 'textarea', placeholder: 'Any additional notes...' },
    ],
    presiteFieldDefs: [
      { key: 'deviceAudit', label: 'Device Audit', type: 'textarea' },
      { key: 'networkTopology', label: 'Network Topology Notes', type: 'textarea' },
    ],
    postQuoteFieldDefs: [
      { key: 'ateraTicketId', label: 'Atera Ticket ID', type: 'text' },
    ],
  },

  [DealCategory.SMART_HOME]: {
    category: DealCategory.SMART_HOME,
    label: 'Smart Home',
    icon: 'Home',
    color: 'amber',
    presiteDefault: 'always',
    intakeFieldDefs: [
      { key: 'description', label: 'Description of Needs', type: 'textarea', required: true },
      { key: 'notes', label: 'Notes', type: 'textarea', placeholder: 'Any additional notes...' },
    ],
    presiteFieldDefs: [
      { key: 'deviceList', label: 'Device List/Plan', type: 'textarea' },
    ],
    postQuoteFieldDefs: [
      { key: 'escalatedToProject', label: 'Escalated to Project?', type: 'boolean' },
      { key: 'linkedProjectId', label: 'Linked Project ID', type: 'text' },
    ],
  },

  // NOTE: enum key remains SECURITY for data back-compat; label is "Surveillance".
  [DealCategory.SECURITY]: {
    category: DealCategory.SECURITY,
    label: 'Surveillance',
    icon: 'Camera',
    color: 'red',
    presiteDefault: 'always',
    intakeFieldDefs: [
      { key: 'estimatedCameraCount', label: 'Camera Count', type: 'button-group', options: ['1-4', '4-8', '8+'], required: true },
      { key: 'cameraInfo', label: 'Camera Details', type: 'textarea', placeholder: 'Indoor/outdoor mix, locations, etc.' },
      { key: 'notes', label: 'Notes', type: 'textarea', placeholder: 'Any additional notes...' },
    ],
    presiteFieldDefs: [
      { key: 'confirmedLocations', label: 'Confirmed Locations', type: 'textarea' },
      { key: 'nvrRequired', label: 'NVR Required?', type: 'boolean' },
    ],
    postQuoteFieldDefs: [],
  },

  [DealCategory.ACCESS_CONTROL]: {
    category: DealCategory.ACCESS_CONTROL,
    label: 'Access Control',
    icon: 'KeyRound',
    color: 'orange',
    presiteDefault: 'always',
    intakeFieldDefs: [
      { key: 'accessPointCount', label: 'Doors / Access Points', type: 'button-group', options: ['1-2', '3-5', '6+'], required: true, allowCustom: true },
      { key: 'accessType', label: 'Access Type', type: 'button-group', options: ['Keypad', 'Fob / Card', 'Intercom', 'Mobile / App', 'Biometric'], required: false, allowCustom: true },
      { key: 'notes', label: 'Notes', type: 'textarea', placeholder: 'Any additional notes...' },
    ],
    presiteFieldDefs: [
      { key: 'doorLocations', label: 'Door / Entry Locations', type: 'textarea' },
      { key: 'powerAvailable', label: 'Power at Door?', type: 'boolean' },
    ],
    postQuoteFieldDefs: [],
  },

  [DealCategory.AV]: {
    category: DealCategory.AV,
    label: 'AV',
    icon: 'Speaker',
    color: 'fuchsia',
    presiteDefault: 'always',
    intakeFieldDefs: [
      { key: 'avType', label: 'AV Type', type: 'button-group', options: ['TV / Display Mount', 'Home Theatre', 'Multi-Room Audio', 'Projector & Screen', 'Outdoor AV', 'Commercial AV'], required: true, allowCustom: true },
      { key: 'displayZoneCount', label: 'Displays / Zones', type: 'button-group', options: ['1', '2-3', '4+'], required: false, allowCustom: true },
      { key: 'notes', label: 'Notes', type: 'textarea', placeholder: 'Any additional notes...' },
    ],
    presiteFieldDefs: [
      { key: 'mountingSurfaces', label: 'Mounting Surfaces / Wall Types', type: 'textarea' },
      { key: 'powerCablingAvailable', label: 'Power & Cabling at Locations?', type: 'boolean' },
    ],
    postQuoteFieldDefs: [],
  },

  [DealCategory.WIFI_EXTENSION]: {
    category: DealCategory.WIFI_EXTENSION,
    label: 'WiFi Extension',
    icon: 'Wifi',
    color: 'teal',
    presiteDefault: 'conditional',
    presiteCondition: 'If UniFi APs or NanoBeam selected (not Deco)',
    intakeFieldDefs: [
      { key: 'wifiType', label: 'WiFi Type', type: 'button-group', options: ['Deco', 'UniFi AP', 'NanoBeam'], required: true, allowCustom: true },
      { key: 'notes', label: 'Notes', type: 'textarea', placeholder: 'Any additional notes...' },
    ],
    presiteFieldDefs: [
      { key: 'apCount', label: 'AP Count', type: 'number' },
      { key: 'cableRunLengths', label: 'Cable Run Lengths', type: 'textarea' },
    ],
    postQuoteFieldDefs: [],
  },

  // Generic billable work / service call — not a product line. No service
  // plan, no pre-site by default; line items are built directly in the quote.
  [DealCategory.FEE_FOR_SERVICE]: {
    category: DealCategory.FEE_FOR_SERVICE,
    label: 'Fee For Service',
    icon: 'Receipt',
    color: 'slate',
    presiteDefault: 'never',
    intakeFieldDefs: [
      { key: 'description', label: 'Work Description', type: 'textarea', required: true, placeholder: 'What work is being quoted / charged for...' },
      { key: 'estimatedHours', label: 'Estimated Hours', type: 'number', placeholder: 'e.g. 2' },
      { key: 'notes', label: 'Notes', type: 'textarea', placeholder: 'Any additional notes...' },
    ],
    presiteFieldDefs: [],
    postQuoteFieldDefs: [],
  },
};

/**
 * Determine if a service module requires a pre-site visit
 */
export function doesServiceRequirePresite(
  category: DealCategory,
  intakeFields: Record<string, unknown>
): boolean {
  const config = SERVICE_CONFIGS[category];
  if (!config) return false;

  switch (config.presiteDefault) {
    case 'always':
      return true;
    case 'never':
      return false;
    case 'conditional':
      if (category === DealCategory.VOIP) {
        const complexity = String(intakeFields.complexity ?? '').toLowerCase();
        return complexity === 'complex';
      }
      if (category === DealCategory.WIFI_EXTENSION) {
        const wifiType = String(intakeFields.wifiType ?? '').toLowerCase();
        return wifiType === 'unifi ap' || wifiType === 'nanobeam';
      }
      return false;
    default:
      return false;
  }
}
